// 殼層（SPEC #vms-web-220，VMS UX 評審 2026-10-13 P0-3、P0-4、P1-10、P1-13）：
//   頂列：品牌、範圍切換器（ScopePicker：麵包屑＋樹狀面板＋搜尋）、環境、全域搜尋（⌘K）、通知鈴鐺、使用者選單（我的帳號、語言、時間顯示、登出）
//   左側主選單：三組（監看／設備與場域／組織管理），可收合、記在瀏覽器；選單名 = 頁標題 = 瀏覽器分頁標題
// 範圍預設「全部」（我自己的 + 組織授權給我的 + 別人分享給我的），分組同「我的設備」（lib/myDevices.ts scopeTree）。
import { pageTitle, usePageSubjectValue } from '../../lib/pageTitle';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { needsFirstFactor } from '../../lib/session';
import { useScope } from '../../lib/scope';
import { isOrgAdmin, isShareJoined, joinApi, type MyOrg } from '../../lib/planeApi';
import type { ScopeNode } from '../../lib/myDevices';
import { scopeNodeName } from '../../lib/scopeName';
import { savePendingJoin, takePendingJoin, type PendingJoin } from '../../lib/pendingJoin';
import { emailVerifyRequired, useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';
import { SsoLinkPending } from './SsoLinkPending';
import { endPresentMode, isPresentMode, withoutPresentParam } from '../../lib/present';
import { defaultExpanded, groupedNav, legacyRedirect, navItemOf, saveNavState, savedNavState, visibleNav, type NavContext, type NavGroup } from './nav';
import { ScopePicker } from './ScopePicker';
import { GlobalSearch } from './GlobalSearch';
import { UserMenu } from './UserMenu';
import { useNodeEnrollOrgs } from '../inventory/useNodeEnrollOrgs';
import { LiveEventsProvider } from '../../lib/liveEvents';
import { SensorPopups } from '../events/SensorPopups';
import { multiScreen } from '../../lib/multiScreen';
import { useIsAlarmWindow } from '../../lib/useMultiScreen';
import { PopoutBar } from '../live/MultiScreen';
import { InboxBell } from '../notifications/Inbox';
import { isSimple, mycamNav, simpleDestination } from './mycamMode';
import { useUnseenCount } from '../../lib/eventSeen';
import { AlarmStatesProvider, useAlarmStates } from '../../lib/alarmStates';
import { TreePanel, saveCollapsed, savedCollapsed, type TreePage } from '../orgtree/TreePanel';
import { PageSkeleton, SkeletonLines } from '../../components/Skeleton';
import { useKmgrNav } from '../kmgr/hooks';
import { useSiteRoomNav } from '../siteroom/useSiteRoom';
import { useEnergyNav } from '../energy/hooks';
import { KmgrNavBadge } from '../kmgr/entry';
import { KmgrGlobalStopBar } from '../kmgr/widgets';
import { HandoverBanner } from '../kmgr/handover/HandoverBanner';

/** 有左側「組織樹 / 檢視 / 群組」面板的頁（SPEC #org-tree-ui 第 1 點：即時影像、回放、事件、地圖）。 */
export const TREE_ROUTES = ['/live', '/playback', '/events', '/map'];

type T = (k: string, o?: Record<string, unknown>) => string;

/** 分組節點的名稱（來源 / 外部分享由多語系表翻）。 */
export { scopeNodeName };

/** 範圍切換器一個選項的文字（依層級縮排；組織有 notice 時加註原因；葉節點帶設備數）。 */
export function scopeNodeLabel(t: T, n: ScopeNode): string {
  let label = scopeNodeName(t, n);
  if (n.notice) label += ' · ' + t(`scope.notice_${n.notice}`, { defaultValue: n.notice });
  return '\u3000'.repeat(n.depth) + label + (n.kind === 'source' || n.kind === 'org' ? '' : ` (${n.count})`);
}

/** 「我的設備」/「我的組織」→ 選單可見條件（nav.ts）。 */
export function navContextOf(
  homes: { role?: string; can_manage?: boolean; org_kind?: string }[],
  devices: { actions: { action: string }[]; children?: { actions: string[] }[] }[],
  orgs: MyOrg[],
): NavContext {
  const team = orgs.filter((o) => o.kind === 'team' && !o.notice);
  const perms = new Set<string>();
  for (const o of team) {
    if (isOrgAdmin(o)) ['org.devices', 'org.sites', 'org.audit', 'org.billing', 'org.support'].forEach((p) => perms.add(p));
    (o.org_permissions ?? []).forEach((p) => perms.add(p));
  }
  const personal = homes.filter((h) => h.org_kind === 'personal');
  return {
    actions: new Set(devices.flatMap((d) => [...d.actions.map((a) => a.action), ...(d.children ?? []).flatMap((c) => c.actions)])),
    homeAdmin: homes.some((h) => h.can_manage || h.role === 'owner' || h.role === 'admin'),
    homeMember: homes.some((h) => h.role === 'owner' || h.role === 'admin' || h.role === 'member'),
    teamAdmin: team.some(isOrgAdmin),
    teamPerms: perms,
    nodeEnroll: team.some((o) => o.node_enroll === true),
    personalOwner: personal.some((h) => h.role === 'owner'),
    personalHome: personal.some((h) => !!h.role),
  };
}

export function Shell() {
  const { t } = useTranslation();
  const { config, uiMode, setUIMode, session } = useApp();
  const { homes, devices, orgs, planes, failed, booting, scopeNotice, clearScopeNotice } = useScope();
  const loc = useLocation();
  const navigate = useNavigate();
  // 展示模式（SPEC #vms-web-demo-jp）：?present=1 記在本分頁，不畫環境標籤、多一顆〔展示結束〕
  const present = useMemo(() => isPresentMode(), []);
  // MyCam 簡易模式（SPEC #mycam-617）：不在簡易模式裡的網址換到攝影機頁
  const simple = isSimple(config, uiMode);
  useEffect(() => {
    if (!simple) return;
    const to = simpleDestination(loc.pathname);
    if (to !== loc.pathname) navigate(to, { replace: true });
  }, [simple, loc.pathname, navigate]);
  const playback = loc.pathname === '/playback';
  const [toastTarget, setToastTarget] = useState<HTMLDivElement | null>(null);
  const withTree = TREE_ROUTES.some((r) => loc.pathname === r || loc.pathname.startsWith(r + '/'));
  const [drawer, setDrawer] = useState(false);
  // 地圖頁、回放頁的左樹收合各自記、各有預設（第三輪 R3-P1-6、B3-12）
  const treePage: TreePage = loc.pathname.startsWith('/map') ? 'map' : playback ? 'playback' : '';
  const [collapsedBy, setCollapsedBy] = useState<Record<string, boolean>>({});
  const collapsed = collapsedBy[treePage] ?? savedCollapsed(treePage);
  const setCollapsed = (v: boolean) => (setCollapsedBy((m) => ({ ...m, [treePage]: v })), saveCollapsed(v, treePage));
  // 〔管理版面…〕要打開左欄「檢視」分頁：收合著就先展開（TreePanel 掛上後自己收同一個事件；這裡補發一次給剛掛上的面板）
  useEffect(() => {
    const on = (e: Event) => {
      if (!collapsed) return;
      setCollapsed(false);
      const detail = (e as CustomEvent).detail;
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('kinio-open-tree-tab', { detail })), 0);
    };
    window.addEventListener('kinio-open-tree-tab', on);
    return () => window.removeEventListener('kinio-open-tree-tab', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, treePage]);
  const qc = useQueryClient();
  const unseen = useUnseenCount();
  // LIVE-10 多螢幕：只有警報視窗彈警報；彈出的牆是精簡外框（沒有頂列、導覽與樹）
  const alarmHere = useIsAlarmWindow();
  const popout = multiScreen.popout;
  const [joinMsg, setJoinMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const enrollOrgs = useNodeEnrollOrgs(orgs);
  const kmgrNav = useKmgrNav();
  const siteroom = useSiteRoomNav();
  const energy = useEnergyNav();
  const ctx = useMemo(
    () => ({ ...navContextOf(homes, devices, orgs), kmgr: kmgrNav.visible, siteroom, energy }),
    [homes, devices, orgs, kmgrNav.visible, siteroom, energy],
  );
  const items = mycamNav(visibleNav(ctx), simple);
  const groups = groupedNav(items);
  const visibleKeys = useMemo(() => new Set(items.map((i) => i.key)), [items]);
  const [navState, setNavState] = useState(savedNavState);
  const isOpen = (g: NavGroup) => (g === 'watch' ? true : (navState[g] ?? defaultExpanded(g, ctx)));
  const toggleGroup = (g: NavGroup) => {
    const next = { ...navState, [g]: !isOpen(g) };
    setNavState(next);
    saveNavState(next);
  };
  const current = navItemOf(loc.pathname);
  const pageName = loc.pathname.startsWith('/settings')
    ? t('nav.account')
    : loc.pathname.startsWith('/support')
      ? t('support.title')
      : current
        ? t(current.labelKey ?? `nav.${current.key}`)
        : '';

  // 分頁標題 = 「<頁名> · <品牌>」；載入中就是品牌名（不顯示空白或網址）
  const subject = usePageSubjectValue();
  useEffect(() => {
    document.title = pageTitle(config.display_name, pageName, subject, booting);
  }, [booting, pageName, subject, config.display_name]);

  // 自動切換範圍的提示（組織膠囊、深連結提示…）：幾秒後自己收
  useEffect(() => {
    if (!scopeNotice) return;
    const id = setTimeout(clearScopeNotice, 5_000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeNotice?.seq]);

  // 換頁回到頂端（B-30：沿用上一頁的捲動位置，標題被切掉）；同一頁換分頁 / 查詢參數不動，網址有 #錨點就交給瀏覽器
  useEffect(() => {
    if (loc.hash) return;
    document.querySelector('main.main')?.scrollTo?.(0, 0);
    window.scrollTo?.(0, 0);
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // 接受邀請頁記下的邀請（已有帳號的人）→ 登入後自動接受。email 還沒驗證（403 EMAIL_NOT_VERIFIED，platform#176）→
  // 「先驗證你的 email」、邀請留著（之後重新整理 / 再登入會再試），對話框發現其實已驗證過就直接重試。
  const pendingJoin = useRef<PendingJoin | null>(null);
  const joinVerify = useEmailVerifyPrompt(() => pendingJoin.current && runJoin(pendingJoin.current));
  function runJoin(pj: PendingJoin) {
    // 有 env 就只打那個 plane（這個 VMS 沒有那個 plane 就不試別的）
    const api = pj.env ? config.planes?.[pj.env] : planes[0]?.api;
    if (!api) return;
    joinApi
      .join(api, pj.token)
      .then((r) => {
        setJoinMsg({
          ok: true,
          text: isShareJoined(r) ? t('activate.done_share') : r.org_name ? t('activate.joined', { name: r.org_name }) : t('activate.joined_short'),
        });
        void qc.invalidateQueries({ queryKey: ['me-orgs'] });
        void qc.invalidateQueries({ queryKey: ['me-devices'] });
      })
      .catch((e) => {
        const c = isKinioError(e) ? e.code : 'default';
        if (emailVerifyRequired(e)) {
          pendingJoin.current = pj;
          savePendingJoin(pj.token, pj.env);
          joinVerify.ask(e);
        }
        setJoinMsg({ ok: false, text: t(`activate.err_${c}`, { code: c, defaultValue: t('activate.err_default', { code: c }) }) });
      });
  }
  useEffect(() => {
    const pj = takePendingJoin();
    if (pj) runJoin(pj);
    // 只在登入後跑一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 舊網址轉址（書籤、通知、email 裡的連結照用；SPEC #vms-web-220 轉址表）
  const legacy = legacyRedirect(loc.pathname, loc.search);
  if (legacy) return <Navigate to={legacy + loc.hash} replace />;

  return (
    <LiveEventsProvider>
      <AlarmStatesProvider>
        <div className={popout ? 'shell popout' : 'shell' + (withTree ? ' shell-tree' + (collapsed ? ' tree-collapsed' : '') : '')}>
          {!popout && (
          <>
          <header className="topbar">
            <div className="brand">
              {config.theme.logo && <img src={config.theme.logo} alt="" />}
              <span>{config.display_name}</span>
            </div>
            <ScopePicker />
            {withTree && (
              <button
                className={'btn btn-sm ot-scope-btn' + (collapsed ? ' collapsed' : '')}
                aria-expanded={drawer}
                title={t('ot.panel_expand')}
                aria-label={t('ot.tree_btn')}
                onClick={() => (window.matchMedia?.(playback ? '(max-width: 1100px)' : '(max-width: 767px)').matches ? setDrawer(true) : setCollapsed(false))}
                data-testid="scope-drawer-btn"
              >
                {/* 打開組織樹（不叫「範圍」，免得跟範圍選擇器同名；第五輪 B5-12 / B5-20） */}
                <span aria-hidden>☰</span> <span className="ot-scope-btn-text">{t('ot.tree_btn')}</span>
              </button>
            )}
            {config.env !== 'prod' && !present && <span className="badge badge-env">{t('shell.env_badge', { env: config.env })}</span>}
            {present && (
              <button
                className="btn btn-sm"
                data-testid="present-end"
                onClick={() => {
                  endPresentMode();
                  location.replace(withoutPresentParam(location.href));
                }}
              >
                {t('shell.present_end')}
              </button>
            )}
            <GlobalSearch
              visibleNav={visibleKeys}
              extra={{ nodeEnroll: enrollOrgs.length > 0, inventory: ctx.teamPerms.has('org.devices'), peopleAdmin: visibleKeys.has('people') }}
            />
            <div className="user">
              {config.mycam?.active && (
                <button className="btn" data-testid="mycam-mode-toggle" onClick={() => setUIMode(simple ? 'full' : 'simple')}>
                  {t(simple ? 'mycam.full_mode' : 'mycam.simple_mode')}
                </button>
              )}
              <InboxBell toastTarget={playback ? toastTarget : null} />
              <UserMenu />
            </div>
          </header>
          <nav className="sidenav" aria-label="main">
            {booting && <SkeletonLines n={8} label={t('common.loading')} />}
            {!booting &&
              groups.map((g) => {
                const open = isOpen(g.group);
                return (
                  <div key={g.group} className="nav-group" data-group={g.group}>
                    {g.group === 'watch' && groups.length === 1 ? null : (
                      <button
                        type="button"
                        className="nav-group-title"
                        aria-expanded={open}
                        disabled={g.group === 'watch'}
                        onClick={() => toggleGroup(g.group)}
                        data-testid={'nav-group-' + g.group}
                      >
                        {t(g.labelKey)}
                        {g.group !== 'watch' && <span aria-hidden>{open ? '▾' : '▸'}</span>}
                      </button>
                    )}
                    {open &&
                      g.items.map((it) => (
                        <NavLink key={it.key} to={it.path} end={it.path === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
                          <span>{t(it.labelKey ?? `nav.${it.key}`)}</span>
                          {it.key === 'events' && <AlarmNavBadge />}
                          {it.key === 'kmgr-incidents' && <KmgrNavBadge />}
                          {it.key === 'events' && unseen ? (
                            <span className="nav-badge" data-testid="events-unseen" title={t('events.unseen_n', { n: unseen })}>
                              {unseen > 99 ? '99+' : unseen}
                            </span>
                          ) : null}
                        </NavLink>
                      ))}
                  </div>
                );
              })}
          </nav>
          {withTree && !collapsed && <TreePanel onCollapse={() => setCollapsed(true)} />}
          {withTree && drawer && (
            <div className="ot-drawer" role="dialog" aria-modal="true" aria-label={t('ot.tree_btn')} data-testid="scope-drawer">
              <div className="ot-drawer-head">
                <strong>{t('ot.tree_btn')}</strong>
                <button className="icon-btn" aria-label={t('common.close')} onClick={() => setDrawer(false)}>
                  ×
                </button>
              </div>
              <TreePanel onPicked={() => setDrawer(false)} />
            </div>
          )}
          </>
          )}
          <main className={'main' + (withTree && !popout ? ' with-tree' : '')}>
            {popout && <PopoutBar alarmHere={alarmHere} />}
            {scopeNotice && (
              <p className="scope-notice" role="status" data-testid="scope-notice">
                {scopeNotice.text}
              </p>
            )}
            {joinVerify.prompt}
            {joinMsg && (
              <p className={joinMsg.ok ? 'notice card' : 'error card'} role="status">
                {joinMsg.text}{' '}
                <button className="link-btn" onClick={() => setJoinMsg(null)}>
                  {t('common.close')}
                </button>
              </p>
            )}
            {failed.length > 0 && <p className="error card">{t('scope.load_failed')}</p>}
            <OrgNotices orgs={orgs} firstFactor={needsFirstFactor(session?.user)} />
            <SsoLinkPending />
            {/* 第一次載入範圍時畫骨架：頁面不先顯示 0 / 空清單再跳（B-29） */}
            {/* Kinio 全域關閉：操作台每頁頂的紅條（SPEC #kinio-kms-global-stop；沒有 kmgr.read 的人 dashboard 沒資料、不畫） */}
            {!booting && kmgrNav.visible && <KmgrGlobalStopBar />}
            {/* 代建中的組織：代建者與接手客戶每頁頂端的接手條（SPEC #kinio-kms-handover，M5-03） */}
            {!booting && <HandoverBanner />}
            {booting ? <PageSkeleton label={t('common.loading')} /> : <Outlet />}
            {alarmHere && <SensorPopups />}
          </main>
          {playback && !popout && <div ref={setToastTarget} className="pb-notification-rail" />}
        </div>
      </AlarmStatesProvider>
    </LiveEventsProvider>
  );
}

/**
 * 組織的安全政策這次登入不符（SPEC #org-model-policy、DP07 / OM09）：該組織照樣列出、設備不列，顯示原因與〔前往設定〕。
 * 只影響那個組織；個人與其他組織照常。
 */
// firstFactor（SSO-18）：公司帳號建的新帳號沒有自己的登入方式——「開兩步驟驗證 / 重新驗證」都做不到，改成引導設第一把 passkey
export function OrgNotices({ orgs, firstFactor = false }: { orgs: Pick<MyOrg, 'org_id' | 'display_name' | 'notice'>[]; firstFactor?: boolean }) {
  const { t } = useTranslation();
  const blocked = orgs.filter((o) => !!o.notice);
  if (!blocked.length) return null;
  return (
    <div className="org-notices" data-testid="org-notices">
      {blocked.map((o) => (
        <p key={o.org_id} className="notice card" data-org-notice={o.notice ?? ''}>
          <strong>{o.display_name}</strong>
          {t('common.colon')}
          {t(`scope.notice_body_${o.notice}`, { defaultValue: t(`scope.notice_${o.notice}`, { defaultValue: o.notice ?? '' }) })}{' '}
          {firstFactor && (o.notice === 'mfa_required' || o.notice === 'session_reauth') ? (
            <Link to={'/sso/first-factor?return=' + encodeURIComponent(window.location.pathname + window.location.search)} data-testid="org-notice-first-factor">
              {t('sso.ff_go')}
            </Link>
          ) : (
            o.notice === 'mfa_required' && <Link to="/settings">{t('scope.go_settings')}</Link>
          )}
          {o.notice === 'sso_required' && <Link to={'/sso/step-up?org=' + encodeURIComponent(o.org_id)}>{t('scope.go_sso')}</Link>}
        </p>
      ))}
    </div>
  );
}

/** 選單「事件」旁的未處理警報數（EVT-06：open + acknowledged）。 */
function AlarmNavBadge() {
  const { t } = useTranslation();
  const { unhandled, hasUnhandled } = useAlarmStates();
  // 還在算次數、平台說有未處理的：先放「…」占位，不留空白（第十輪 B10-01）
  if (unhandled === null && hasUnhandled)
    return (
      <span className="nav-badge alarm" data-testid="alarms-unhandled" data-counting="1" title={t('alarm.unhandled_counting')}>
        …
      </span>
    );
  if (!unhandled) return null;
  return (
    <span className="nav-badge alarm" data-testid="alarms-unhandled" title={t('alarm.unhandled_n', { n: unhandled })}>
      {unhandled > 99 ? '99+' : unhandled}
    </span>
  );
}
